import { useTranslation } from 'react-i18next';

import { relativeWindow } from './pitchWindow';
import {
  axisRows,
  guideBlocks,
  guideRange,
  headPoint,
  tracePaths,
} from './traceGeometry';

import type { PitchGraphPoint } from '../../../components';
import type { SungRange } from '../capture';
import type { MelodyNote } from '../melody';

/**
 * PitchTraceGraph — **노래 진단**(`RecordViz: 'trace'`)의 시각화. 시간에 따른 음정
 * 곡선을 그린다.
 *
 * 이 진단이 재는 것은 얼마나 오래냐가 아니라 **정답을 따라갔나**다. 그래서 지나간
 * 궤적이 화면에 남는다 — 예전엔 여기도 막대 하나가 `발성시간 / 제한시간`으로 자라서,
 * 오래 버틴 사람이 잘한 것처럼 보였다.
 *
 * **축은 고정이다 — 곡 전체가 한 화면에 깔린다.** 시안(`987:5516` → `1017:17872`)은
 * 흐르는 축(스크롤 + 고정 커서)이지만 실기기 확인에서 정적 축이 낫다는 판정으로
 * 되돌렸다: 짧은 진단 곡은 전곡이 보이는 편이 다음에 부를 음을 미리 읽기 좋고, 커서가
 * 걸어가는 것으로 "지금"도 충분히 읽힌다.
 *
 * **왼쪽에 음이름 축을 세운다 — 예전의 "절대 음높이는 안 보여준다" 결정을 뒤집었다.**
 * 그 결정의 근거는 음이름이 판단에 안 쓰인다는 것이었는데, 노래 진단에서는 성립하지
 * 않는다: 정답 블록과 내 곡선이 **어느 음에서** 어긋났는지가 곧 피드백이다. 시안
 * (`1017:17872`)도 왼쪽에 `C3`~`B3`를 세운다. 세로 창은 여전히 `pitchWindow`가 잡는다 —
 * 옥타브 단위로만 자라고 중심이 격자에 붙어서 축이 미끄러지지 않는다.
 *
 * **프롭 구동** — 네이티브 이벤트를 스스로 구독하지 않는다. 구독처는 `useVoiceCapture`
 * 하나뿐이라 mock 브리지(브라우저 개발 경로)도 자동으로 같은 화면을 본다. 앱 공용
 * `RealtimePitchGraph`를 그대로 쓰지 않는 이유가 이것이다 — 그쪽은 스스로 구독하고
 * 음이름·주파수 눈금까지 그려서 두 계약을 동시에 깬다. 순수 계산만 나눠 쓴다.
 */

/** 음이름 축이 차지하는 왼쪽 폭. 그리는 영역은 그만큼 안쪽에서 시작한다. */
const PLOT_LEFT = '2.75rem';

interface PitchTraceGraphProps {
  /** 캡처가 누적한 창. 테이크 전체가 들어 있다(livePoints의 창 길이 계약). */
  points: PitchGraphPoint[];
  /** 이번 테이크에서 부른 폭 — 세로 창은 이것만 보고 잡는다. */
  sung: SungRange | null;
  /** 가로축 원점(ms) — 테이크의 첫 프레임 타임스탬프다. 멜로디 t=0도 여기 얹힌다. */
  startMs: number;
  /** 화면 폭이 담는 시간(초) — 곡 전체가 한 화면에 깔린다. */
  spanSec: number;
  /**
   * 부를 정답 멜로디(노래 진단에만). 회색 블록으로 미리 깔고 그 위에 곡선을 겹친다 —
   * 겹치면 맞은 것이라, 설명 없이 읽힌다.
   */
  guide?: MelodyNote[];
}

export const PitchTraceGraph = ({
  points,
  sung,
  startMs,
  spanSec,
  guide,
}: PitchTraceGraphProps) => {
  const { t } = useTranslation();
  const axis = { startMs, spanMs: spanSec * 1000 };
  // 가이드 음까지 감싸야 아직 안 부른 목표가 화면 밖으로 안 나간다.
  const window = relativeWindow(sung, guide ? guideRange(guide) : null);
  const paths = tracePaths(points, axis, window);
  const head = headPoint(points, axis, window);
  const blocks = guide ? guideBlocks(guide, axis, window) : [];
  const rows = axisRows(window);

  return (
    <div
      role="img"
      aria-label={t('web.diagnosis.track.traceLabel')}
      // 백분율 높이를 쓰지 않는다 — 셸 루트가 `min-h-dvh`라 조상 높이가 indefinite이고
      // 거기서 `height:100%`는 0이 된다(PitchBarTrack에 같은 주석). 호출자가 flex
      // 컨테이너로 감싸고 여기서는 교차축 stretch로 늘어난다.
      className="relative w-full overflow-hidden"
    >
      {/* 안쪽 한 겹을 위아래로 들여 놓는다. 바깥 상자에 `padding`을 주는 것으로는 안 된다 —
          절대 배치의 `top: %`는 **패딩 박스** 기준이라 `top: 0%`가 여전히 맨 끝이고, 축
          라벨은 각 음에 중심을 맞춰 놓이므로 창 양 끝의 음이름이 절반만 보인다. */}
      <div className="absolute inset-x-0 bottom-3 top-3">
        {/* 음이름 축 — 시안의 `C3`~`B3`. 창이 넓어지면 라벨이 성기게 간다(axisRows). */}
        {rows.map((row) => (
          <span
            key={row.midi}
            data-testid="trace-axis-note"
            aria-hidden="true"
            className="absolute left-0 -translate-y-1/2 text-[13px] tabular-nums text-[var(--bk-200)]"
            style={{ top: `${row.y}%` }}
          >
            {row.label}
          </span>
        ))}

        {/* 그리는 영역을 **자기 상자**로 분리한다. 축 폭을 좌표마다 더하고 빼면(`calc(2.5rem
          + x% * 0.88)`) 곡선·블록·도트가 각자 다른 식을 갖게 되고, 축 폭을 바꿀 때 한
          군데만 고쳐져 서로 어긋난다. 상자를 밀어 두면 안쪽 좌표는 그대로 0..100이다. */}
        <div
          // `overflow-hidden` — 흘러간 가이드 블록·곡선이 축 라벨 위로 넘어오지 않게.
          className="absolute inset-y-0 right-0 overflow-hidden"
          style={{ left: PLOT_LEFT }}
          aria-hidden="true"
        >
          {/* 격자는 축 라벨과 **짝**이다(시안: 반음마다 가로선) — 라벨이 성기게 가면
              선도 같이 성기게 가서, 선만 있고 어느 음인지 모르는 줄이 안 생긴다. */}
          {rows.map((row) => (
            <div
              key={row.midi}
              className="absolute inset-x-0 h-px bg-border/60"
              style={{ top: `${row.y}%` }}
            />
          ))}

          {/* 가이드는 곡선 **아래**에 깔린다 — 겹쳤을 때 내 목소리가 위로 보여야 한다.
            블록을 SVG 안에 넣지 않는 이유: 세로로 늘어나는 좌표계라 모서리 반경까지 같이
            찌그러진다. 사각형이라 %만으로 놓을 수 있으니 HTML로 얹는다. */}
          {blocks.map((block) => (
            <div
              key={block.id}
              data-testid="pitch-guide-note"
              className="absolute h-3.5 -translate-y-1/2 rounded-full bg-[var(--bk-100)]"
              style={{
                left: `${block.x}%`,
                width: `${block.width}%`,
                top: `${block.y}%`,
              }}
            />
          ))}

          {/* 좌표는 0..100 view 단위다(traceGeometry). 컨테이너를 재지 않으므로 곡선 계산이
            레이아웃에 안 묶이고, 늘어나며 생기는 선 굵기 왜곡은 non-scaling-stroke가 막는다. */}
          <svg
            // **`h-full w-full`은 `inset-0`과 중복이 아니라 필수다.** `<svg>`는 replaced
            // element라 viewBox가 종횡비(1:1)를 주는데, 그 경우 절대 배치의 `bottom` 오프셋이
            // 무시되고 높이가 너비를 따라간다 — 390×544 트랙에서 곡선만 위쪽 390px에 그려지고,
            // %로 배치되는 도트·기준선과 좌표계가 어긋난다(실측).
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {/* **키는 `d`가 아니라 순번이다.** 축이 흐르면서 모든 점의 x가 매 프레임
              바뀌므로 `d`도 매 프레임 바뀐다 — 그걸 키로 쓰면 React가 프레임마다 모든
              `<path>`를 버리고 다시 만든다. 구간은 시간 순으로 뒤에만 붙으므로 순번이
              안정적인 이름이다(같은 좌표의 한 프레임짜리 구간 둘이 키를 나눠 갖는 일도
              같이 사라진다). */}
            {paths.map((d, i) => (
              <path
                key={i}
                data-testid="pitch-trace"
                d={d}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={4}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>

          {head != null && (
            <span
              data-testid="pitch-trace-head"
              // SVG 안에 원을 그리면 축이 따로 늘어나 타원이 된다 — 도트만 HTML로 얹는다.
              className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.5)]"
              style={{ left: `${head.x}%`, top: `${head.y}%` }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
